import React, { useState, useEffect } from "react";
import { IntlProvider } from "react-intl";
import { jwtDecode } from "jwt-decode";
import { Helmet as HelmetAsync, HelmetProvider } from "react-helmet-async";
import { useIdleTimer } from "react-idle-timer";
import { CHARITY, COMPANY } from "./constants";
import {
  getBrowserLocale,
  millisecondsToMinutesSeconds,
  getLocalStorageItem,
  setLocalStorageItem,
  decodeString,
} from "./utils/utilities";
import {
  getLoginData,
  getProfileData,
  getProfileDataCompany,
  resetState,
} from "./redux/actions";
import { loginSelector } from "./redux/selectors";
import { useAppDispatch, useAppSelector } from "./redux/store";
import locale from "./locale/locale";
import RootRoute from "./RootRoute";
import TimeOutModal from "./components/TimeOutModal";

const App = () => {
  const browserLocale = getBrowserLocale() ?? "en";
  const messages = locale?.[browserLocale] ?? locale?.en;
  const dispatch = useAppDispatch();
  const loginObj = useAppSelector(loginSelector) ?? {};
  const IDToken = loginObj?.IDToken ?? null;

  const [userType, setUserType] = useState(null);
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);
  const [remainingTime, setRemainingTime] = useState(null);
  const [tokenTimer, setTokenTimer] = useState(null);

  const { getRemainingTime, reset } = useIdleTimer({
    timeout: 1000 * 60 * 14,
    debounce: 500,
    onIdle: () => {
      if (window?.location?.pathname?.includes("public")) return;

      window.open(`${window.location.origin}/login?loggedOut`, "_self");
      localStorage.clear();
      sessionStorage.clear();
    },
    events: ["keydown", "mousedown", "touchstart"],
  });

  const triggerLogout = () => {
    window.open(`${window.location.origin}/login?loggedOut`, "_self");
    localStorage.clear();
    sessionStorage.clear();
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (window?.location?.pathname?.includes("public")) return;

      const remaining = getRemainingTime();
      if (remaining < 1000 * 60 * 1) {
        setRemainingTime(remaining);
        setShowTimeoutModal(true);
      } else {
        setShowTimeoutModal(false);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [getRemainingTime]);

  const handleCharityLoginRoutes = () => dispatch(getProfileData());
  const handleCompanyLoginRoutes = () => dispatch(getProfileDataCompany());

  useEffect(() => {
    if (IDToken) {
      let decodedToken;
      try {
        decodedToken = jwtDecode(IDToken);
      } catch (error) {
        console.error("Failed to decode ID token:", error);
        return;
      }

      setLocalStorageItem("device_key", loginObj?.DeviceKey ?? "");
      setLocalStorageItem("userType", decodedToken?.["custom:entity"] ?? "");
      setLocalStorageItem("companyID", decodedToken?.["custom:entityID"] ?? "");
      setLocalStorageItem("id_Token", IDToken);
      setLocalStorageItem("user_details", decodedToken?.email ?? "");

      const userType = getLocalStorageItem("userType");
      if (userType === CHARITY) {
        setUserType(CHARITY);
        handleCharityLoginRoutes();
      } else if (userType === COMPANY) {
        setUserType(COMPANY);
        handleCompanyLoginRoutes();
      }

      if (tokenTimer) clearTimeout(tokenTimer);
      const newTimer = setTimeout(() => {
        setShowTimeoutModal(true);
        setRemainingTime(60000);
      }, 1000 * 60 * 59);

      setTokenTimer(newTimer);
    }
  }, [IDToken, dispatch]);

  const handleLogoutClick = () => {
    dispatch(resetState());
    triggerLogout();
    localStorage.clear();
    sessionStorage.clear();
    window.open(`${window.location.origin}/login`, "_self");
    setShowTimeoutModal(false);
  };

  const handleStayLoggedInClick = () => {
    let data;
    try {
      data = JSON.parse(getLocalStorageItem("userDetails")) ?? {};
    } catch (error) {
      console.error("Failed to parse user details from local storage:", error);
      return;
    }

    const loginData = {
      email: data?.email ?? "",
      password: decodeString(data?.password ?? ""),
    };
    dispatch(getLoginData(loginData));
    reset();

    if (tokenTimer) clearTimeout(tokenTimer);
    const newTimer = setTimeout(() => {
      setShowTimeoutModal(true);
      setRemainingTime(60000);
    }, 1000 * 60 * 59);
    setTokenTimer(newTimer);
  };

  return (
    <HelmetProvider>
      <HelmetAsync>
        <title>BizGive</title>
      </HelmetAsync>
      <IntlProvider locale={browserLocale} messages={messages}>
        <RootRoute userType={userType} />
        {showTimeoutModal && (
          <TimeOutModal
            handleLogoutClick={handleLogoutClick}
            handleStayLoggedInClick={handleStayLoggedInClick}
            remainingTime={millisecondsToMinutesSeconds(remainingTime ?? 0)}
            showTimeoutModal={showTimeoutModal}
          />
        )}
      </IntlProvider>
    </HelmetProvider>
  );
};

export default App;
